// Undo for the canvas.
//
// Every canvas edit is a pure function from one `StudioDoc` to the next, so
// history is a list of documents: no command objects or inverse operations, so
// a new edit cannot forget to implement its own undo.
//
// Bounded (`HISTORY_LIMIT`) because the stack lives as long as the tab and grows
// with every keystroke of a chart title.
//
// Generic over the document type, so its tests are about the stack.

/** How many past states are kept. Past this the oldest is dropped, which is a
 *  real loss of the ability to undo that far and is the accepted trade: the
 *  alternative is a stack that grows without limit for the life of the tab. */
export const HISTORY_LIMIT = 50;

export interface History<T> {
  past: readonly T[];
  present: T;
  future: readonly T[];
}

export function newHistory<T>(present: T): History<T> {
  return { past: [], present, future: [] };
}

/**
 * Record a new state.
 *
 * A push CLEARS the redo stack, which is the ordinary editor contract: once you
 * undo three steps and then type, the three you undid are no longer reachable,
 * because the document they applied to no longer exists.
 *
 * A push of a state EQUAL to the present is a no-op. Without that, a component
 * that re-renders and re-pushes (or an edit that resolves to the same document,
 * like re-selecting the aggregate that was already set) fills the stack with
 * duplicates and makes undo feel broken — you press it four times and nothing
 * moves.
 */
export function pushHistory<T>(history: History<T>, next: T, equal: (a: T, b: T) => boolean): History<T> {
  if (equal(history.present, next)) return history;
  const past = [...history.past, history.present];
  return {
    past: past.length > HISTORY_LIMIT ? past.slice(past.length - HISTORY_LIMIT) : past,
    present: next,
    future: [],
  };
}

export function canUndo<T>(history: History<T>): boolean {
  return history.past.length > 0;
}

export function canRedo<T>(history: History<T>): boolean {
  return history.future.length > 0;
}

/** Step back one state. A no-op at the beginning of history, returning the SAME
 *  object so a caller can compare by identity and skip a re-render. */
export function undo<T>(history: History<T>): History<T> {
  if (history.past.length === 0) return history;
  const previous = history.past[history.past.length - 1] as T;
  return {
    past: history.past.slice(0, -1),
    present: previous,
    future: [history.present, ...history.future],
  };
}

export function redo<T>(history: History<T>): History<T> {
  if (history.future.length === 0) return history;
  const next = history.future[0] as T;
  return {
    past: [...history.past, history.present],
    present: next,
    future: history.future.slice(1),
  };
}

/**
 * Start again from a state the SERVER acknowledged.
 *
 * Not the same thing as a push, and collapsing the two is a real bug: after a
 * save, a reload or a restore, the states before it describe a document the
 * server no longer has, so an undo through them would silently re-introduce
 * edits somebody had just discarded, or edits that lost a 409. History restarts
 * at the acknowledged document.
 */
export function resetHistory<T>(present: T): History<T> {
  return newHistory(present);
}

/** Whether a keyboard event is the undo or the redo gesture, or neither.
 *
 *  Both spellings of redo are accepted because both are in muscle memory:
 *  shift+cmd+z on a Mac, ctrl+y on Windows. Anything typed into a field is NOT
 *  a canvas gesture — a person undoing inside a chart title means the title,
 *  and stealing that would make the text boxes feel broken. */
export function historyGesture(event: {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  target: EventTarget | null;
}): "undo" | "redo" | null {
  if (isTextEntry(event.target)) return null;
  if (!event.metaKey && !event.ctrlKey) return null;
  const key = event.key.toLowerCase();
  if (key === "y") return "redo";
  if (key !== "z") return null;
  return event.shiftKey ? "redo" : "undo";
}

/** Duck-typed rather than `instanceof HTMLElement`, deliberately: this module is
 *  pure and its tests run without a DOM, where `HTMLElement` is not defined at
 *  all and `instanceof` throws rather than answering false. */
function isTextEntry(target: EventTarget | null): boolean {
  const element = target as { tagName?: unknown; isContentEditable?: unknown } | null;
  if (!element || typeof element.tagName !== "string") return false;
  if (element.isContentEditable === true) return true;
  const tag = element.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select";
}
